/**
 * Solar design settings service (SD-8.1, FR-33, FR-39).
 *
 * READS AND WRITES `solar_design_settings`, NEVER `tenant_settings` — the story states it
 * because the 20260803 migration deliberately moved these columns OFF tenant_settings, and the
 * one thing that would quietly undo that is a service that still points at the old table. The
 * access gate (`design_access_role_ids`) stays on tenant_settings per FR-28 and is NOT this
 * service's concern; it belongs to the SD-8.3 access card.
 *
 * A MISSING ROW IS A STATE, NOT AN ERROR (UX-DR17, detected ON LOAD): the 20260803 trigger
 * backfills every tenant, so a missing row means the tenant was created by a path that bypassed
 * it. `getSolarDesignSettings` answers `{ missing: true }` and the screen renders the
 * `save_uninitialised` string; save is update-only, so this service can never mask that state
 * by inserting a row the trigger should have made.
 *
 * NO CACHE, deliberately, where tenantSettingsService carries one: this row is read on one
 * admin screen, and a cached read after another admin's save would show stale values under a
 * badge claiming freshness ("yours, Allan, 12 Mar") — wrong in exactly the way UX-DR18 exists
 * to prevent.
 */

import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import type { DesignSettingsValues } from '@/components/settings/features/design/designSettingsForm';

export interface SolarDesignSettingsRow extends DesignSettingsValues {
  tenant_id: string;
  updated_by: string | null;
  updated_at: string | null;
}

export type SolarDesignSettingsResult =
  | { missing: false; row: SolarDesignSettingsRow; editorName: string | null }
  | { missing: true };

/** PostgREST returns NUMERIC as strings; the form logic wants numbers everywhere. */
export function normaliseRow(raw: Record<string, unknown>): SolarDesignSettingsRow {
  const n = (v: unknown): number => (typeof v === 'string' ? Number(v) : (v as number));
  return {
    tenant_id: raw.tenant_id as string,
    setback_ridge_in: n(raw.setback_ridge_in),
    setback_eave_in: n(raw.setback_eave_in),
    setback_rake_in: n(raw.setback_rake_in),
    utility_rate_per_kwh: n(raw.utility_rate_per_kwh),
    utility_escalation_pct: n(raw.utility_escalation_pct),
    price_per_watt: n(raw.price_per_watt),
    default_module_id: (raw.default_module_id as string | null) ?? null,
    updated_by: (raw.updated_by as string | null) ?? null,
    updated_at: (raw.updated_at as string | null) ?? null,
  };
}

/**
 * The row plus the editor's first name for the UX-DR18 badge.
 *
 * The name comes from the `users` table (the project rule: never user_profiles for core user
 * data), and a failed lookup degrades to null — the badge renders "yours, 12 Mar" rather than
 * blocking the screen on a join.
 */
export async function getSolarDesignSettings(): Promise<SolarDesignSettingsResult | null> {
  try {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) {
      console.error('[solarDesignSettingsService] No tenant ID available');
      return null;
    }

    const { data, error } = await supabase
      .from('solar_design_settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();

    if (error) {
      console.error('[solarDesignSettingsService] Failed to fetch settings:', error);
      return null;
    }
    if (!data) return { missing: true };

    const row = normaliseRow(data);

    let editorName: string | null = null;
    if (row.updated_by) {
      const { data: editor } = await supabase
        .from('users')
        .select('first_name')
        .eq('id', row.updated_by)
        .maybeSingle();
      editorName = editor?.first_name ?? null;
    }

    return { missing: false, row, editorName };
  } catch (error) {
    console.error('[solarDesignSettingsService] Error in getSolarDesignSettings:', error);
    return null;
  }
}

/**
 * UPDATE-ONLY, never upsert: an insert here would paper over the missing-row state the screen
 * is required to surface. `updated_by`/`updated_at` are stamped by the table trigger; sending
 * them from the client would be lying about who the stamp trigger says edited.
 */
export async function updateSolarDesignSettings(
  values: Partial<DesignSettingsValues>
): Promise<SolarDesignSettingsRow> {
  const tenantId = await getCurrentTenantId();
  if (!tenantId) throw new Error('No tenant ID available');

  const { data, error } = await supabase
    .from('solar_design_settings')
    .update(values)
    .eq('tenant_id', tenantId)
    .select()
    .single();

  if (error) throw error;
  return normaliseRow(data);
}
